import { Filter, Loader2, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/utils';

export type EmptyStateVariant =
  | 'zero'
  | 'filter'
  | 'subsection'
  | 'calm'
  | 'dependency'
  | 'loading';

export interface EmptyStateCTA {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
}

export interface EmptyStateProps {
  /** Defaults to 'zero' for back-compat with pre-LOK-1098 call sites. */
  variant?: EmptyStateVariant;
  /** Required except when `variant='loading'`. */
  title?: string;
  description?: string;
  /** Hero icon. Ignored when `variant='loading'` or `'subsection'`. */
  icon?: LucideIcon;
  /**
   * Primary CTA. Disallowed when `variant='calm'` (dev-warn).
   * For `variant='filter'`, label MUST be a "Clear filter" equivalent
   * (dev-warn if it starts with "Create" / "New" / "+").
   */
  primaryCTA?: EmptyStateCTA;
  secondaryCTA?: EmptyStateCTA;
  /** Required when `variant='dependency'`. Names the upstream blocker. */
  dependencyHint?: string;
  /**
   * @deprecated Use `primaryCTA` and/or `secondaryCTA`. Pre-LOK-1098 call
   * sites still pass an `action` ReactNode; we keep rendering it but
   * dev-warn so the migration is greppable.
   */
  action?: ReactNode;
  /** Optional educational hint (e.g. <BeginnerHint>) shown below the action. */
  hint?: ReactNode;
  /** For `variant='loading'`: how many skeleton rows to render (default 5). */
  skeletonRows?: number;
  className?: string;
}

const FORBIDDEN_FILTER_PREFIXES = ['Create', 'New', '+'];

function devWarn(message: string) {
  if (import.meta.env.DEV) {
    console.warn(`[EmptyState] ${message}`);
  }
}

function checkInvariants(props: EmptyStateProps) {
  if (!import.meta.env.DEV) return;
  const { variant = 'zero', primaryCTA, dependencyHint, description } = props;

  if (variant === 'filter' && primaryCTA) {
    const labelStartsForbidden = FORBIDDEN_FILTER_PREFIXES.some((p) =>
      primaryCTA.label.trim().startsWith(p),
    );
    if (labelStartsForbidden) {
      devWarn(
        `variant='filter' must use a "Clear filter" CTA — got "${primaryCTA.label}". ` +
          'Showing "+ Create new" when data is filter-hidden is gaslighting (Gallery 1, identity rule 2).',
      );
    }
  }

  if (variant === 'calm' && primaryCTA) {
    devWarn(
      `variant='calm' must not have a primaryCTA — got "${primaryCTA.label}". ` +
        'Empty is the good state; quiet celebration only.',
    );
  }

  if (variant === 'dependency' && !dependencyHint) {
    devWarn(`variant='dependency' requires \`dependencyHint\` to name the prerequisite by name.`);
  }

  if (description && description.length > 240) {
    devWarn(`description is ${description.length} chars; UI truncates above 240.`);
  }
}

export function EmptyState(props: EmptyStateProps) {
  checkInvariants(props);

  const {
    variant = 'zero',
    title,
    description,
    icon: Icon,
    primaryCTA,
    secondaryCTA,
    dependencyHint,
    action,
    hint,
    skeletonRows = 5,
    className,
  } = props;

  if (action !== undefined && (primaryCTA || secondaryCTA)) {
    devWarn('`action` and `primaryCTA`/`secondaryCTA` are both set — drop `action` (deprecated).');
  } else if (action !== undefined) {
    devWarn('`action` prop is deprecated. Migrate to `primaryCTA={{ label, onClick }}`.');
  }

  // ── Loading variant ────────────────────────────────────────────────────
  if (variant === 'loading') {
    return (
      <div className={cn('w-full space-y-2 py-2', className)} aria-live="polite" aria-busy="true">
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  // ── Subsection variant — compact card, no hero ─────────────────────────
  if (variant === 'subsection') {
    return (
      <div
        className={cn(
          'rounded-md border border-dashed border-line bg-bg-2/40',
          'flex flex-col items-center justify-center gap-2 py-8 px-4 text-center',
          className,
        )}
      >
        {title && <p className="text-sm font-medium text-text-70">{title}</p>}
        {description && <p className="text-[13px] text-text-55 max-w-md">{description}</p>}
        {hint && <div className="max-w-md text-left">{hint}</div>}
      </div>
    );
  }

  // ── Hero variants: zero / filter / calm / dependency ───────────────────
  const heroVariantPadding = variant === 'filter' ? 'py-10' : 'py-12';
  const HeroIcon = Icon ?? (variant === 'filter' ? Filter : null);
  const heroBgClass =
    variant === 'calm'
      ? 'bg-[radial-gradient(circle,rgba(34,197,94,0.18),rgba(34,197,94,0.02))]'
      : 'bg-[radial-gradient(circle,rgba(245,166,35,0.18),rgba(245,166,35,0.02))]';
  const heroIconColor = variant === 'calm' ? 'text-ok-text' : 'text-gold';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 text-center',
        heroVariantPadding,
        className,
      )}
    >
      {HeroIcon && (
        <div className={cn('flex h-24 w-24 items-center justify-center rounded-full', heroBgClass)}>
          <HeroIcon className={cn('h-10 w-10', heroIconColor)} aria-hidden />
        </div>
      )}
      <div className="max-w-[460px]">
        {title && <p className="text-base font-medium text-text">{title}</p>}
        {description && <p className="mt-1 text-sm text-text-70">{description}</p>}
        {variant === 'dependency' && dependencyHint && (
          <p className="mt-2 inline-block rounded-md bg-bg-2 px-2 py-1 font-mono text-[12px] text-text-55">
            {dependencyHint}
          </p>
        )}
      </div>

      {/* New canonical CTA slots */}
      {(primaryCTA || secondaryCTA) && (
        <div className="flex items-center gap-2">
          {primaryCTA && (
            <Button onClick={primaryCTA.onClick}>
              {primaryCTA.icon && <primaryCTA.icon className="mr-2 size-4" aria-hidden />}
              {primaryCTA.label}
            </Button>
          )}
          {secondaryCTA && (
            <Button variant="ghost" onClick={secondaryCTA.onClick}>
              {secondaryCTA.icon && <secondaryCTA.icon className="mr-2 size-4" aria-hidden />}
              {secondaryCTA.label}
            </Button>
          )}
        </div>
      )}

      {/* Legacy slot (back-compat with pre-LOK-1098 call sites) */}
      {action && !primaryCTA && !secondaryCTA && <div>{action}</div>}

      {hint && <div className="max-w-md text-left">{hint}</div>}
    </div>
  );
}

/**
 * Skeleton row count helper for `variant='loading'` callers. Re-exported
 * so the dev gallery and tests can render it without ad-hoc duplication.
 */
export { Loader2 as EmptyStateLoadingIcon };
